import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, Target, FileText, ListChecks, MessageSquare, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TIPO_CONFIG = {
  oportunidade: { icon: Target, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-300', label: 'Oportunidade' },
  proposta: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-300', label: 'Proposta' },
  tarefa: { icon: ListChecks, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-300', label: 'Tarefa' },
  interacao: { icon: MessageSquare, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-300', label: 'Interação' },
};

export const HistoricoClienteModal = ({ clienteId, clienteNome, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('todos');

  useEffect(() => {
    if (open && clienteId) {
      loadData();
    }
  }, [open, clienteId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/configuracoes/historico-cliente/${clienteId}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const filteredTimeline = (data?.timeline || []).filter(item =>
    filtro === 'todos' || item.tipo === filtro
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            Histórico: {clienteNome}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : data ? (
          <div className="space-y-4 mt-2">
            {/* Summary KPIs */}
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(TIPO_CONFIG).map(([key, cfg]) => {
                const count = data.resumo?.[key === 'interacao' ? 'interacoes' : `${key}s`] || 0;
                return (
                  <button
                    key={key}
                    onClick={() => setFiltro(f => f === key ? 'todos' : key)}
                    data-testid={`filter-${key}`}
                    className={`rounded-lg p-2 text-center transition-all border ${
                      filtro === key ? `${cfg.border} ${cfg.bg}` : 'border-transparent hover:bg-slate-50'
                    }`}
                  >
                    <cfg.icon className={`w-4 h-4 mx-auto mb-0.5 ${cfg.color}`} />
                    <p className="text-lg font-bold text-slate-700">{count}</p>
                    <p className="text-[10px] text-slate-500">{cfg.label}s</p>
                  </button>
                );
              })}
            </div>

            {/* Timeline */}
            <div className="relative pl-6 space-y-3">
              <div className="absolute left-2.5 top-0 bottom-0 w-px bg-slate-200" />
              {filteredTimeline.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Nenhum registro encontrado.</p>
              ) : (
                filteredTimeline.map((item, i) => {
                  const cfg = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.interacao;
                  const Icon = cfg.icon;
                  return (
                    <div key={i} className="relative" data-testid={`timeline-item-${i}`}>
                      <div className={`absolute -left-3.5 w-5 h-5 rounded-full ${cfg.bg} border-2 ${cfg.border} flex items-center justify-center`}>
                        <Icon className={`w-2.5 h-2.5 ${cfg.color}`} />
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 ml-2">
                        <div className="flex items-center gap-2 mb-0.5">
                          <Badge variant="outline" className={`text-[9px] ${cfg.color} ${cfg.bg}`}>{cfg.label}</Badge>
                          <span className="text-[10px] text-slate-400">
                            {item.data ? new Date(item.data).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                          </span>
                          {item.vendedor && <span className="text-[10px] text-slate-400">| {item.vendedor}</span>}
                        </div>
                        <p className="text-sm font-medium text-slate-700">{item.titulo}</p>
                        {item.detalhe && <p className="text-xs text-slate-500 mt-0.5">{item.detalhe}</p>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <p className="text-[10px] text-slate-400 text-center">
              {data.total_eventos} evento(s) registrado(s) • Histórico preservado mesmo com troca de vendedor
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8">Nenhum histórico encontrado para este cliente.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
